/**
 * Create an order round: a name, the closing date and time (South African
 * time) and the words staff see at the top of their order page. The standard
 * catalogue is loaded with it; prices and items are adjusted afterwards.
 */
import { useId, useState, type FormEvent } from 'react'
import { Loader2, Plus } from 'lucide-react'
import toast from 'react-hot-toast'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { errorDetail, fromSastParts } from '@/lib/staffOrder'
import { staffOrderService, type StaffOrderRoundDetail } from '@/services/api/staffOrderService'

interface CreateRoundFormProps {
  onCreated: (detail: StaffOrderRoundDetail) => void
  onCancel?: () => void
}

export function CreateRoundForm({ onCreated, onCancel }: CreateRoundFormProps) {
  const [name, setName] = useState('')
  const [date, setDate] = useState('')
  const [time, setTime] = useState('23:59')
  const [intro, setIntro] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const ids = { name: useId(), date: useId(), time: useId(), intro: useId() }

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    if (saving) return
    setError(null)
    if (!name.trim()) {
      setError('Give the order round a name.')
      return
    }
    let closesAt: string | null = null
    if (date) {
      closesAt = fromSastParts(date, time)
      if (!closesAt) {
        setError('Enter a valid closing date and time, or leave the date empty to set it later.')
        return
      }
    }
    setSaving(true)
    try {
      const detail = await staffOrderService.createRound({
        name: name.trim(),
        closes_at: closesAt,
        intro_text: intro.trim() || null,
      })
      toast.success('Order round created.')
      onCreated(detail)
    } catch (err) {
      setError(errorDetail(err, 'The order round could not be created. Please try again.'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <form onSubmit={submit} className="space-y-4">
      <div className="space-y-1.5">
        <Label htmlFor={ids.name}>Name</Label>
        <Input
          id={ids.name}
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Staff apparel 2026"
          maxLength={200}
        />
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label htmlFor={ids.date}>Closing date</Label>
          <Input id={ids.date} type="date" value={date} onChange={(e) => setDate(e.target.value)} />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor={ids.time}>Closing time (South African time)</Label>
          <Input id={ids.time} type="time" value={time} onChange={(e) => setTime(e.target.value)} />
        </div>
      </div>
      <p className="-mt-2 text-xs text-gray-500">
        You can set the closing date later, but ordering cannot open without one.
      </p>
      <div className="space-y-1.5">
        <Label htmlFor={ids.intro}>Words at the top of the order page</Label>
        <Textarea
          id={ids.intro}
          rows={5}
          value={intro}
          onChange={(e) => setIntro(e.target.value)}
          placeholder="What staff read before they choose. Line breaks are kept."
        />
      </div>
      <p className="text-xs text-gray-500">
        The standard catalogue of items is added for you. You can change prices and switch items
        off once the order round exists.
      </p>
      {error && (
        <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700" role="alert">
          {error}
        </div>
      )}
      <div className="flex flex-wrap justify-end gap-2">
        {onCancel && (
          <Button type="button" variant="outline" onClick={onCancel} disabled={saving}>
            Cancel
          </Button>
        )}
        <Button type="submit" disabled={saving}>
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
          Create order round
        </Button>
      </div>
    </form>
  )
}
